/**
 * UploadCase — Neuer Sprint-B-Flow:
 *
 *   Pflicht: Fallbeschreibung (Textarea, ≥20 Zeichen)
 *   Optional: PDF-Upload (max 20 MB)
 *
 * Ohne PDF → POST /api/upload/text-only legt eine cases-Row mit chunk_count=0
 * an. Trial-Endpoint überspringt dann den User-RAG-Schritt sauber.
 *
 * Mit PDF  → bisheriger Pfad (POST /api/upload + POST /api/upload/:id/parse).
 *
 * Für die Schnellfrage-Section im Dashboard gibt es eine separate, schlankere
 * QuickQuestion-Komponente. Diese hier deckt den kompletten Hauptfluss ab.
 */
import { useCallback, useState } from "react";
import { uploadCase, parseCase, createTextOnlyCase, type TextOnlyCaseType } from "../lib/api";

interface ParseInfo {
  indexedChunks: number;
  totalPages: number;
  totalCharacters: number;
}

interface Props {
  onUploaded: (caseId: string, info?: ParseInfo, caseContext?: string) => void;
}

const CASE_TYPES: { value: TextOnlyCaseType; label: string }[] = [
  { value: "rent_reduction", label: "Mietminderung" },
  { value: "flight_delay", label: "Flugverspätung" },
  { value: "product_defect", label: "Produktmangel" },
  { value: "consumer_dispute", label: "Verbraucherstreit" },
  { value: "employment", label: "Arbeitsrecht" },
  { value: "other", label: "Sonstiges" },
];

export default function UploadCase({ onUploaded }: Props) {
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [caseType, setCaseType] = useState<TextOnlyCaseType>("consumer_dispute");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<"idle" | "creating" | "uploading" | "parsing">("idle");
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = useCallback(
    (f: File) => {
      if (f.type !== "application/pdf") {
        setError("Nur PDF-Dateien werden akzeptiert.");
        return;
      }
      if (f.size > 20 * 1024 * 1024) {
        setError("Datei ist größer als 20 MB.");
        return;
      }
      setError(null);
      setFile(f);
      if (!title) setTitle(f.name.replace(/\.pdf$/i, ""));
    },
    [title]
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const canSubmit = description.trim().length >= 20 && !loading;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      const effectiveTitle =
        title.trim() ||
        description.trim().replace(/\s+/g, " ").slice(0, 80) ||
        "Fall ohne Titel";

      if (file) {
        // Pfad mit PDF: original Upload + Parse
        setPhase("uploading");
        const res = await uploadCase(file, { title: effectiveTitle, caseType });
        setPhase("parsing");
        let parseInfo: ParseInfo | undefined;
        try {
          const parsed = await parseCase(res.caseId);
          parseInfo = {
            indexedChunks: parsed.indexedChunks,
            totalPages: parsed.totalPages,
            totalCharacters: parsed.totalCharacters,
          };
        } catch (parseErr) {
          // Best-effort: Trial läuft auch ohne lokalen RAG
          console.warn("[upload] parse failed, continuing", parseErr);
        }
        onUploaded(res.caseId, parseInfo, description.trim());
      } else {
        // Pfad ohne PDF: text-only case
        setPhase("creating");
        const res = await createTextOnlyCase({
          title: effectiveTitle,
          caseType,
          description: description.trim(),
        });
        onUploaded(res.caseId, undefined, description.trim());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setPhase("idle");
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div>
        <label className="block text-sm text-slate-300 mb-1">
          Sachverhalt <span className="text-red-400">*</span>{" "}
          <span className="text-slate-500">(was ist passiert? Min. 20 Zeichen)</span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={5}
          required
          minLength={20}
          placeholder="z. B. Ich habe am 12.03.2026 ein iPhone 15 bei Beispiel GmbH gekauft. Nach 4 Wochen lädt es nicht mehr zuverlässig. Mehrere Aufforderungen zur Nachbesserung blieben ohne Reaktion."
          className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500 font-mono"
        />
        <p className="mt-1 text-xs text-slate-500">
          {description.trim().length}/20 Zeichen erreicht
          {description.trim().length >= 20 && " ✓"}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-slate-300 mb-1">
            Kurz-Titel <span className="text-slate-500">(optional, sonst aus Beschreibung)</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z. B. iPhone 15 Garantie"
            className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500"
          />
        </div>
        <div>
          <label className="block text-sm text-slate-300 mb-1">Falltyp</label>
          <select
            value={caseType}
            onChange={(e) => setCaseType(e.target.value as TextOnlyCaseType)}
            className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500"
          >
            {CASE_TYPES.map((ct) => (
              <option key={ct.value} value={ct.value}>
                {ct.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm text-slate-300 mb-1">
          Dokument <span className="text-slate-500">(optional — Vertrag, Rechnung, E-Mails als PDF)</span>
        </label>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={
            "rounded-xl border-2 border-dashed p-5 text-center transition " +
            (dragOver
              ? "border-court-400 bg-court-900/30"
              : "border-slate-700 bg-slate-900/40")
          }
        >
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            className="hidden"
            id="pdf-input"
          />
          <label htmlFor="pdf-input" className="cursor-pointer block">
            {file ? (
              <div className="text-sm">
                <p className="font-semibold text-white">{file.name}</p>
                <p className="text-slate-400 mt-1">
                  {(file.size / 1024 / 1024).toFixed(2)} MB · PDF
                </p>
                <p className="mt-2 text-xs text-court-300">
                  Klicken oder ziehen, um auszutauschen ·{" "}
                  <button
                    type="button"
                    onClick={(ev) => {
                      ev.preventDefault();
                      setFile(null);
                    }}
                    className="text-amber-400 hover:underline"
                  >
                    Entfernen
                  </button>
                </p>
              </div>
            ) : (
              <div>
                <p className="text-slate-300 text-sm">
                  PDF hierher ziehen oder{" "}
                  <span className="text-court-300 underline">durchsuchen</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Max 20 MB · ohne Dokument läuft die Jury allein auf der Beschreibung
                </p>
              </div>
            )}
          </label>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-950/50 border border-red-800 px-4 py-2 text-sm text-red-200">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="w-full rounded-lg bg-gradient-to-r from-court-500 to-court-700 hover:from-court-400 hover:to-court-600 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-3 font-semibold transition shadow-lg shadow-court-700/30"
      >
        {phase === "creating"
          ? "Lege Fall an…"
          : phase === "uploading"
          ? "Lade PDF hoch…"
          : phase === "parsing"
          ? "Extrahiere Text & indexiere…"
          : file
          ? "Fall + PDF speichern und zur Jury weiter"
          : "Fall speichern und zur Jury weiter"}
      </button>
    </form>
  );
}
